// 파일 화면(§9.4) 한 칸의 **선택 규칙**과 **상태 표시줄 수치** — 순수 함수만 둔다(단위 시험이 `node --test` 로 바로 불러 쓴다).
//
// 선택은 일반 탐색기(Windows 탐색기·Finder)와 같게 한다(2026-09-29 사용자 요구 — 종전엔 누를 때마다 더하기/빼기였다):
//   - 그냥 클릭        = 그 항목**만** 선택(기준점 = 그 항목)
//   - Ctrl/⌘ + 클릭    = 그 항목을 더하거나 뺀다(기준점 = 그 항목)
//   - Shift + 클릭     = 기준점부터 그 항목까지 범위**만** 선택(기준점은 그대로)
//   - Ctrl/⌘ + Shift   = 지금 선택에 범위를 **더한다**
//   - 목록의 빈 곳 클릭 = 선택 해제

import { formatBytes, type DirItem } from './protocol.ts';

export type SelectMode = 'only' | 'toggle' | 'range' | 'addRange';

export const SELECT_MODES: readonly SelectMode[] = ['only', 'toggle', 'range', 'addRange'];

/** 클릭한 순간의 수정키 → 선택 방식. Mac 은 ⌘(metaKey), 그 밖은 Ctrl — 둘 다 받는다(원격이 아니라 내 화면의 조작이다). */
export function selectModeOf(e: { shiftKey: boolean; ctrlKey: boolean; metaKey: boolean }): SelectMode {
  const add = e.ctrlKey || e.metaKey;
  if (e.shiftKey) {
    return add ? 'addRange' : 'range';
  }
  return add ? 'toggle' : 'only';
}

export interface Selection {
  selected: string[];
  /** Shift 범위의 기준점(마지막으로 그냥/Ctrl 클릭한 항목). 없으면 null. */
  anchor: string | null;
}

/**
 * 클릭 한 번 뒤의 선택.
 * @param items 지금 칸의 목록(화면 순서 — 범위는 이 순서로 잡는다)
 * @param cur   지금 선택
 * @param name  클릭한 항목 이름
 * @param mode  `selectModeOf` 결과
 */
export function nextSelection(items: DirItem[], cur: Selection, name: string, mode: SelectMode): Selection {
  const idx = items.findIndex((it) => it.name === name);
  if (idx < 0) {
    return cur; // 목록이 막 바뀌어 없는 이름 — 그대로 둔다
  }
  switch (mode) {
    case 'only':
      return { selected: [name], anchor: name };
    case 'toggle':
      return {
        selected: cur.selected.includes(name) ? cur.selected.filter((n) => n !== name) : [...cur.selected, name],
        anchor: name,
      };
    case 'range':
    case 'addRange': {
      // 기준점이 없거나 목록에서 사라졌으면 그 항목이 기준점이다(탐색기와 같다).
      const a = cur.anchor === null ? -1 : items.findIndex((it) => it.name === cur.anchor);
      const from = a < 0 ? idx : a;
      const [lo, hi] = from <= idx ? [from, idx] : [idx, from];
      const range = items.slice(lo, hi + 1).map((it) => it.name);
      const selected = mode === 'range' ? range : [...cur.selected, ...range.filter((n) => !cur.selected.includes(n))];
      return { selected, anchor: a < 0 ? name : cur.anchor };
    }
  }
}

/**
 * 지금 폴더가 어느 드라이브(볼륨) 아래인가 — 칸 머리 드라이브 목록의 현재 값. 없으면(최상위·범위 밖) 빈 문자열.
 * 여러 개가 맞으면 **가장 긴 것**(Mac `/` 와 `/Volumes/USB` 가 둘 다 맞을 때 USB).
 * Windows 경로(`C:\`)는 대소문자를 가리지 않는다.
 */
export function rootOf(dir: string, roots: string[]): string {
  let best = '';
  for (const r of roots) {
    const hit = /^[A-Za-z]:\\/.test(r) ? dir.toLowerCase().startsWith(r.toLowerCase()) : isUnder(dir, r);
    if (hit && r.length > best.length) {
      best = r;
    }
  }
  return best;
}

/**
 * 드라이브 목록에 보일 이름 — `C:\` → `C:` · `/` → `/ (시스템)` · `/Volumes/USB` → `USB` · 그 밖(보드 `/sdcard`)은 그대로.
 */
export function rootLabel(root: string): string {
  if (/^[A-Za-z]:\\$/.test(root)) {
    return root.slice(0, 2);
  }
  if (root === '/') {
    return '/ (시스템)';
  }
  const vol = /^\/Volumes\/(.+)$/.exec(root);
  return vol ? vol[1] : root;
}

/** `/Volumes/US` 가 `/Volumes/USB` 를 잡지 않게 — 경계가 구분자일 때만 아래로 본다. */
function isUnder(dir: string, root: string): boolean {
  if (!dir.startsWith(root)) {
    return false;
  }
  return root.endsWith('/') || dir.length === root.length || dir[root.length] === '/';
}

/** 상태 표시줄 수치. 폴더 크기는 목록이 알려 주지 않는다(0) — 용량은 **파일만** 더한다. */
export interface PanelStats {
  dirs: number;
  files: number;
  /** 파일 크기 합(바이트). */
  bytes: number;
  selected: number;
  /** 고른 것 중 폴더 수 — 용량에 들어가지 않는다는 표시에 쓴다. */
  selectedDirs: number;
  /** 고른 파일 크기 합(바이트). */
  selectedBytes: number;
}

/**
 * 상태 표시줄 문구 — 왼쪽 = 이 폴더 전체, 오른쪽 = 선택(없으면 빈 문자열).
 * 예) "폴더 12 · 파일 30 · 4.2 MB"  /  "3개 선택 (폴더 1) · 1.2 MB"
 * 폴더를 고르면 그 크기는 알 수 없으므로 "(폴더 n)" 으로 따로 적고 용량은 파일만 더한다.
 */
export function describeStats(s: PanelStats): { all: string; picked: string } {
  const all = `폴더 ${s.dirs} · 파일 ${s.files} · ${formatBytes(s.bytes)}`;
  if (s.selected === 0) {
    return { all, picked: '' };
  }
  const dirs = s.selectedDirs > 0 ? ` (폴더 ${s.selectedDirs})` : '';
  const bytes = s.selected > s.selectedDirs ? ` · ${formatBytes(s.selectedBytes)}` : '';
  return { all, picked: `${s.selected}개 선택${dirs}${bytes}` };
}

export function panelStats(items: DirItem[], selected: string[]): PanelStats {
  const pick = new Set(selected);
  const s: PanelStats = { dirs: 0, files: 0, bytes: 0, selected: 0, selectedDirs: 0, selectedBytes: 0 };
  for (const it of items) {
    if (it.dir) {
      s.dirs++;
    } else {
      s.files++;
      s.bytes += it.size;
    }
    if (pick.has(it.name)) {
      s.selected++;
      if (it.dir) {
        s.selectedDirs++;
      } else {
        s.selectedBytes += it.size;
      }
    }
  }
  return s;
}
